<template lang="pug">
transition(name="modal-fade")
  .feedback-modal-overlay(v-if="isVisible" @click.self="handleCancel")
    .feedback-modal
      .modal-header
        h3 Compartilhe sua experiência
        .close-btn(@click="handleCancel")
          LucideIcon(name="X" :size="20")
      
      .modal-body
        .info-text
          p Sua opinião é muito importante para melhorarmos a plataforma!
        
        .rating-section
          label.label Avaliação *
          .stars-container(@click="enableTextarea")
            LucideIcon(
              v-for="index in 5"
              :key="index"
              name="Star"
              :size="32"
              :fill="index <= selectedRating ? '#facc15' : 'none'"
              :stroke="index <= selectedRating ? '#facc15' : '#3f3f46'"
              @click="selectRating(index)"
              class="star-icon"
            )
        
        .textarea-section
          label.label Feedback *
          textarea(
            v-model="feedbackText"
            :disabled="!textareaEnabled"
            :maxlength="150"
            placeholder="Conte-nos sobre sua experiência... (mínimo 5 caracteres)"
            rows="4"
          )
          .char-count(:class="{ 'warning': remainingChars < 20 }") {{ remainingChars }} caracteres restantes
      
      .modal-footer
        ButtonPadrao(variant="negative" @click="handleCancel") Cancelar
        ButtonPadrao(@click="handleSubmit" :disabled="!isFormValid" :loading="isSubmitting") Enviar
</template>

<script>
import api from '@/services/api'
import MessageMixin from '@/mixins/MessageMixin'
import ButtonPadrao from '@/components/ui/ButtonPadrao.vue'

export default {
  name: 'FeedbackModal',
  components: {
    ButtonPadrao
  },
  mixins: [MessageMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedRating: 0,
      feedbackText: '',
      textareaEnabled: false,
      isSubmitting: false
    }
  },
  computed: {
    isVisible() {
      return this.visible
    },
    remainingChars() {
      return 150 - this.feedbackText.length
    },
    isFormValid() {
      return this.selectedRating >= 1 && 
             this.feedbackText.length >= 5 && 
             !this.isSubmitting
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
        this.resetForm()
      }
    }
  },
  methods: {
    selectRating(rating) {
      this.selectedRating = rating
      this.textareaEnabled = true
    },
    enableTextarea() {
      if (this.selectedRating >= 1) {
        this.textareaEnabled = true
      }
    },
    handleCancel() {
      this.$emit('close')
    },
    async handleSubmit() {
      if (!this.isFormValid) return

      this.isSubmitting = true
      
      try {
        await api.post('/Feedback', {
          feedback: this.feedbackText,
          rating: this.selectedRating
        })
        
        this.showSuccessMessage('Feedback enviado com sucesso! Obrigado pela sua contribuição.')
        this.$emit('close')
        this.resetForm()
      } catch (error) {
        console.error('Erro ao enviar feedback:', error)
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Erro ao enviar feedback. Tente novamente mais tarde.'
        this.showErrorMessage(errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },
    resetForm() {
      this.selectedRating = 0
      this.feedbackText = ''
      this.textareaEnabled = false
      this.isSubmitting = false
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped lang="scss">
.feedback-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  pointer-events: auto;
}

.feedback-modal {
  background: $corFundoPadraoDashboard;
  border: 1px solid $borderColor;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  position: relative;
  z-index: 1001;
}

@keyframes modalAppear {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid $borderColor;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: $corVerdeClaro;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 6px;
    color: $corBranco;
    transition: all 0.2s;

    &:hover {
      background: $corVerdeClaroHover;
      color: $corVerdeClaro;
    }
  }
}

.modal-body {
  padding: 24px;

  .info-text {
    margin-bottom: 24px;
    
    p {
      margin: 0;
      font-size: 14px;
      color: $corValueInfoContato;
      line-height: 1.5;
    }
  }

  .rating-section {
    margin-bottom: 24px;

    .label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: $corBranco;
      margin-bottom: 12px;
    }

    .stars-container {
      display: flex;
      gap: 8px;
      
      .star-icon {
        cursor: pointer;
        transition: transform 0.2s;

        &:hover {
          transform: scale(1.1);
        }
      }
    }
  }

  .textarea-section {
    .label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: $corBranco;
      margin-bottom: 12px;
    }

    textarea {
      width: 100%;
      padding: 12px;
      border: 1px solid $borderColor;
      border-radius: 8px;
      background: $corPreto;
      color: $corBranco;
      font-size: 14px;
      font-family: inherit;
      resize: vertical;
      min-height: 100px;
      box-sizing: border-box;
      transition: all 0.2s;

      &:focus {
        outline: none;
        border-color: $corVerdeClaro;
        box-shadow: 0 0 0 3px rgba(183, 255, 0, 0.1);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &::placeholder {
        color: $corValueInfoContato;
        opacity: 0.6;
      }
    }

    .char-count {
      margin-top: 8px;
      font-size: 12px;
      color: $corValueInfoContato;
      text-align: right;

      &.warning {
        color: #facc15;
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid $borderColor;
}

@media (max-width: 500px) {
  .feedback-modal {
    max-width: calc(100% - 20px);

    .modal-body {
      .rating-section {
        .stars-container {
          gap: 4px;
          
          .star-icon {
            width: 28px;
            height: 28px;
          }
        }
      }
    }
  }

  .modal-footer {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .feedback-modal {
  animation: modalAppear 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-leave-active .feedback-modal {
  animation: modalDisappear 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalDisappear {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
}
</style>
